import React, { useCallback } from 'react';
import { Form } from 'react-bootstrap';
import {
  formatNumericInput,
  parseNumericInput,
  sanitizeNumericTyping,
} from '../../utils/numberInput';

/**
 * Text-based numeric input with comma paste support and US-style blur formatting.
 * Use with Formik: pass name, value, onChange, onBlur from field props.
 *
 * @param {Object} props
 * @param {string} [props.name]
 * @param {string|number} [props.value]
 * @param {function} props.onChange - Receives synthetic event with normalized target.value
 * @param {function} [props.onBlur]
 * @param {boolean} [props.integerOnly=false]
 * @param {boolean} [props.formatOnBlur=true]
 * @param {number} [props.minimumFractionDigits=0]
 * @param {number} [props.maximumFractionDigits=2]
 * @param {string} [props.placeholder]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.isInvalid]
 * @param {boolean} [props.isValid]
 * @param {string} [props.className]
 * @param {string} [props.controlId]
 */
const NumberInput = ({
  name,
  value = '',
  onChange,
  onBlur,
  integerOnly = false,
  formatOnBlur = true,
  minimumFractionDigits = 0,
  maximumFractionDigits = 2,
  placeholder,
  disabled,
  isInvalid,
  isValid,
  className,
  controlId,
  ...rest
}) => {
  const displayValue = value === null || value === undefined ? '' : String(value);

  const emitChange = useCallback(
    (nextValue) => {
      if (!onChange) return;
      onChange({
        target: { name, value: nextValue },
        currentTarget: { name, value: nextValue },
      });
    },
    [onChange, name]
  );

  const handleChange = useCallback(
    (e) => {
      const sanitized = sanitizeNumericTyping(e.target.value, integerOnly);
      emitChange(sanitized);
    },
    [emitChange, integerOnly]
  );

  const handleBlur = useCallback(
    (e) => {
      if (formatOnBlur && displayValue.trim() !== '') {
        const formatted = formatNumericInput(displayValue, {
          integerOnly,
          minimumFractionDigits,
          maximumFractionDigits,
        });
        if (formatted !== displayValue) {
          emitChange(formatted);
        }
      }
      if (onBlur) onBlur(e);
    },
    [
      displayValue,
      emitChange,
      formatOnBlur,
      integerOnly,
      minimumFractionDigits,
      maximumFractionDigits,
      onBlur,
    ]
  );

  const handlePaste = useCallback(
    (e) => {
      const pasted = e.clipboardData?.getData('text');
      if (pasted == null) return;
      e.preventDefault();
      const parsed = parseNumericInput(pasted, { integerOnly });
      if (parsed === null) {
        emitChange(sanitizeNumericTyping(pasted, integerOnly));
        return;
      }
      const formatted = formatNumericInput(parsed, {
        integerOnly,
        minimumFractionDigits,
        maximumFractionDigits,
      });
      emitChange(formatted || String(parsed));
    },
    [emitChange, integerOnly, minimumFractionDigits, maximumFractionDigits]
  );

  return (
    <Form.Control
      type="text"
      inputMode={integerOnly ? 'numeric' : 'decimal'}
      name={name}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onPaste={handlePaste}
      placeholder={placeholder}
      disabled={disabled}
      isInvalid={isInvalid}
      isValid={isValid}
      className={className}
      id={controlId}
      {...rest}
    />
  );
};

export default NumberInput;
